// ---------------------------------------------------------------------------
// PageHeader, ported from the design system (R10)
//
// The top of a screen: where it sits (breadcrumbs, or a back link one level
// down), the title — the page's one h1, tabindex -1 so the router can move
// focus to it, not in the tab order — with its status, one line on what it
// is, then the actions with the one primary action last.
//
//   - breadcrumbs are a nav named "Breadcrumb" (breadcrumbsLabel); the last
//     item without href and onClick is the current page, a span marked
//     aria-current="page"; every other item is a link (href, with onClick
//     for the router) or, without href, a button. A chevron sits before each
//     item but the first;
//   - without breadcrumbs, `back` is a link or button with chevron-left and
//     its label. The system draws chevron-right mirrored only while its icon
//     set lacks chevron-left; the port asks its own icon set the same way;
//   - the actions row is there when actions or primaryAction is truthy, and
//     holds actions, then primaryAction.
//
// A template function: the system's PageHeader holds no state. The props keep
// the system's names; every React-node prop goes through node() (React draws
// true, false and null as nothing). No renames.
//
// crumbLink() is the system's helper of the same name, shared with
// AuthLayout's legal links (auth-layout.ts).
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { cx, node, present } from './helpers.js';
import { ICONS, icon } from './icons.js';

/** A place to go: a link with href (and onClick for the router), else a button with onClick. */
export interface PageLink {
  label: unknown;
  href?: string;
  onClick?: (event: MouseEvent) => void;
}

/** The system's crumbLink: an <a> when the link has an href, else a <button type="button">. */
export function crumbLink(c: PageLink, className: string, content: unknown): TemplateResult {
  return c.href != null
    ? html`<a href=${c.href} class=${className} @click=${c.onClick}>${node(content)}</a>`
    : html`<button type="button" class=${className} @click=${c.onClick}>${node(content)}</button>`;
}

export interface PageHeaderProps {
  /** The page's name, set in display. */
  title: unknown;
  titleId?: string;
  /** Counts and state in one line. */
  description?: unknown;
  /** One StatusBadge. */
  status?: unknown;
  /** The last item without href or onClick is the current page. */
  breadcrumbs?: PageLink[];
  breadcrumbsLabel?: string;
  /** Used when there are no breadcrumbs. */
  back?: PageLink;
  /** Secondary and ghost buttons, a Menu for the rest. */
  actions?: unknown;
  /** The one primary button, shown last. */
  primaryAction?: unknown;
  className?: string;
}

export function pageHeader(p: PageHeaderProps): TemplateResult {
  const crumbs = p.breadcrumbs || [];
  let lead: TemplateResult | null = null;
  if (crumbs.length) {
    lead = html`<nav class="sdr-crumbs" aria-label=${p.breadcrumbsLabel || 'Breadcrumb'}>
      <ol class="sdr-crumbs__list">
        ${crumbs.map((c, i) => {
          const here = i === crumbs.length - 1 && c.href == null && !c.onClick;
          return html`<li class="sdr-crumbs__item">${
            i ? icon({ name: 'chevron-right', size: 12, className: 'sdr-crumbs__sep' }) : null
          }${
            here
              ? html`<span class="sdr-crumbs__current" aria-current="page">${node(c.label)}</span>`
              : crumbLink(c, 'sdr-crumbs__link', c.label)
          }</li>`;
        })}
      </ol>
    </nav>`;
  } else if (p.back) {
    // The system mirrors chevron-right only while its icon set has no chevron-left (R6f brought it).
    const flip = !(ICONS as Record<string, string>)['chevron-left'];
    lead = crumbLink(
      p.back,
      cx('sdr-page-head__back', flip && 'sdr-page-head__back--flip'),
      html`${icon({ name: flip ? 'chevron-right' : 'chevron-left', size: 16 })}<span>${node(p.back.label)}</span>`,
    );
  }
  return html`<header class=${cx('sdr-page-head', p.className)}>
    ${lead ? html`<div class="sdr-page-head__lead">${lead}</div>` : null}
    <div class="sdr-page-head__row">
      <div class="sdr-page-head__titles">
        <div class="sdr-page-head__title-row">
          <h1 id=${ifDefined(p.titleId)} tabindex="-1" class="sdr-page-head__title">${node(p.title)}</h1>
          ${present(p.status) ? html`<span class="sdr-page-head__status">${node(p.status)}</span>` : null}
        </div>
        ${present(p.description) ? html`<p class="sdr-page-head__desc">${node(p.description)}</p>` : null}
      </div>
      ${
        present(p.actions) || present(p.primaryAction)
          ? html`<div class="sdr-page-head__actions">${node(p.actions || null)}${node(p.primaryAction || null)}</div>`
          : null
      }
    </div>
  </header>`;
}
